// router.tsx - Separate file for clean separation of concerns
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { lazy } from "react";
import ErrorFallback from "../Error/ErrorFallback";
import AppInner from "./AppInner";

// ── Authenticated / app scenes ──
const SignUp = lazy(() => import("../../scenes/signup"));
const Login = lazy(() => import("../../scenes/login"));
const Dashboard = lazy(() => import("../../scenes/dashboard"));
const AddProject = lazy(() => import("../../scenes/projectAdd"));
const TransactionAdd = lazy(() => import("../../scenes/transactionAdd"));
const ShareImpact = lazy(() => import("../../scenes/shareImpact"));
const Explore = lazy(() => import("../../scenes/explore"));
const GrantHub = lazy(() => import("../../scenes/grantHub"));
const ProjectPage = lazy(() => import("../../scenes/project"));
const CharityPage = lazy(() => import("../../scenes/charity"));
const DonorOrgPage = lazy(() => import("../../scenes/donorOrg"));
const TermsAndConditions = lazy(
  () => import("../../scenes/landing-page/Terms"),
);
const Privacy = lazy(() => import("../../scenes/landing-page/Privacy"));
const CookiesPage = lazy(() => import("../../scenes/landing-page/Cookies"));
const UserProfile = lazy(() => import("../../scenes/userprofile"));
const Settings = lazy(() => import("../../scenes/userprofile/settings/index"));
const AppLayout = lazy(() => import("./AppLayout"));
const CharityPageLayout = lazy(() => import("./CharityPageLayout"));
const DonorOrgPageLayout = lazy(() => import("./DonorOrgPageLayout"));
const SendUpdateLayout = lazy(() => import("./SendUpdateLayout"));
const ProjectTransactions = lazy(
  () => import("../../scenes/projectTransactions"),
);
const CharityTransactions = lazy(
  () => import("../../scenes/charityTransactions"),
);
const AddProjectUpdate = lazy(
  () => import("../../scenes/sendUpdate/addProjectUpdate"),
);
const AddCharityUpdate = lazy(
  () => import("../../scenes/sendUpdate/addCharityUpdate"),
);
const ProjectUpdate = lazy(() => import("../../scenes/projectUpdate"));
const CharityUpdate = lazy(() => import("../../scenes/charityUpdate"));
const BankingCallback = lazy(
  () => import("../../scenes/transactionAdd/open-banking/BankingCallback"),
);
const CharityDashboard = lazy(() => import("../../scenes/charityDashboard"));
const ProjectManagement = lazy(() => import("../../scenes/projectManagement"));
const DonorOrgDashboard = lazy(() => import("../../scenes/donorOrgDashboard"));
const SubscriptionPage = lazy(
  () => import("../../components/NewCharityDashboard/Subscription/Subscription"),
);
const PrivateRoute = lazy(() => import("../../components/Routes/PrivateRoute"));
const ReportDashboard = lazy(() => import("../../scenes/reportDashboard"));
const ReportViewPage = lazy(
  () => import("../../scenes/reportDashboard/ReportViewPage"),
);
const ReviewExtractedTransactions = lazy(
  () => import("../../scenes/reviewExtractedTransactions"),
);
const DonationPortal = lazy(() => import("../../scenes/donationPortal"));
const DonationSuccess = lazy(
  () => import("../../scenes/donationPortal/donationSuccess"),
);
const AdminStatsPage = lazy(() => import("../../scenes/adminStats"));
const InvitePortal = lazy(() => import("../../scenes/invite-portal"));
const InviteOrgPortal = lazy(() => import("../../scenes/invite-org-portal"));
const ForgotPassword = lazy(() => import("../../scenes/forgotPassword"));
const StripeOAuthCallback = lazy(
  () => import("../../scenes/donationPortal/StripeOAuthCallback"),
);
const PlatformDaoPage = lazy(() => import("../../scenes/platformDao"));

// ── Landing (marketing) scenes + layout ──
const LandingLayout = lazy(() => import("./LandingLayout"));
const Home = lazy(() => import("../../scenes/landing-page/Home"));
const Monitor = lazy(() => import("../../scenes/landing-page/Monitor"));
const About = lazy(() => import("../../scenes/landing-page/About"));
const Manage = lazy(() => import("../../scenes/landing-page/Manage"));
const Report = lazy(() => import("../../scenes/landing-page/Report"));
const Blog = lazy(() => import("../../scenes/landing-page/Blog"));
const BlogPost = lazy(() => import("../../scenes/landing-page/BlogPost"));
const Donor = lazy(() => import("../../scenes/landing-page/Donor"));
const NotFound = lazy(() => import("../../scenes/landing-page/NotFound"));

// ✅ Stable — defined once at module level, never recreated
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppInner />}>
      {/* ── Auth-entry routes (no chrome) ── */}
      <Route path="/register" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invite-portal" element={<InvitePortal />} />
      <Route path="/invite-org-portal" element={<InviteOrgPortal />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* ── Public marketing / landing surface ──
          Wrapped in LandingLayout: marketing Navbar + Footer + Ticker,
          forced Inter font, cream background. Auth-aware nav lives here. */}
      <Route element={<LandingLayout />}>
        <Route index element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/manage" element={<Manage />} />
        <Route path="/monitor" element={<Monitor />} />
        <Route path="/report" element={<Report />} />
        <Route path="/donor" element={<Donor />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/cookies" element={<CookiesPage />} />
        {/* Any unknown public URL renders the landing 404 page,
            keeping SEO behaviour and brand chrome consistent. */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Authenticated app shell with the original navbar/footer ── */}
      <Route element={<AppLayout />}>
        <Route path="/explore" element={<Explore />} />
        <Route path="/grant-hub/*" element={<GrantHub />} />
        <Route path="/grants-hub/*" element={<GrantHub />} />
        <Route
          path="/project/:projectUUID"
          element={<ProjectPage />}
          errorElement={<ErrorFallback />}
        />
        <Route
          path="/projectupdate/:projectUpdateId"
          element={<ProjectUpdate />}
          errorElement={<ErrorFallback />}
        />
        <Route
          path="/charityupdate/:charityUUID/:charityUpdateId"
          element={<CharityUpdate />}
          errorElement={<ErrorFallback />}
        />
        <Route
          path="/project/:projectUUID/transactions"
          element={<ProjectTransactions />}
          errorElement={<ErrorFallback />}
        />
        <Route element={<CharityPageLayout />}>
          <Route path="/charity/:charityUUID" element={<CharityPage />} />
        </Route>
        <Route
          path="/charity/:charityUUID/transactions"
          element={<CharityTransactions />}
          errorElement={<ErrorFallback />}
        />
        <Route path="/project-reporting" element={<ReportDashboard />} />
        <Route path="/reports/view" element={<ReportViewPage />} />

        {/* ── Donation portal (public, app-chrome) ── */}
        <Route path="/donation-portal" element={<DonationPortal />} />
        <Route path="/donation-success" element={<DonationSuccess />} />

        {/* ── Authenticated routes ── */}
        <Route element={<PrivateRoute />}>
          <Route path="/userprofile" element={<UserProfile />} />
          <Route path="/profile-settings" element={<Settings />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/admin/stats" element={<AdminStatsPage />} />
          <Route path="/dashboard-donor" element={<Dashboard />} />
          <Route path="/dashboard-donor-org" element={<DonorOrgDashboard />} />

          <Route element={<DonorOrgPageLayout />}>
            <Route path="/donororg/:donorOrgUUID" element={<DonorOrgPage />} />
          </Route>

          <Route
            path="/dashboard-charity"
            element={<CharityDashboard />}
            errorElement={<ErrorFallback />}
          />
          <Route
            path="/project-management/:projectUUID"
            element={<ProjectManagement />}
            errorElement={<ErrorFallback />}
          />
          <Route path="/addproject" element={<AddProject />} />
          <Route path="/addtransaction" element={<TransactionAdd />} />
          {/* Design sandbox for the post-extraction review step, on fake data. The
              live version is the "Automatic" mode of /addtransaction. */}
          <Route
            path="/review-extracted-transactions"
            element={<ReviewExtractedTransactions />}
          />
          <Route path="/share-impact" element={<ShareImpact />} />
          <Route path="/platform-dao" element={<PlatformDaoPage />} />
          <Route
            path="/charity/banking/callback"
            element={<BankingCallback />}
          />

          <Route element={<SendUpdateLayout />}>
            <Route path="/sendupdate/project" element={<AddProjectUpdate />} />
            <Route path="/sendupdate/charity" element={<AddCharityUpdate />} />
          </Route>

          <Route
            path="/auth/stripe/oauth/callback"
            element={<StripeOAuthCallback />}
          />
        </Route>
      </Route>
    </Route>,
  ),
);
